/**
 * TimelineView.tsx - TimelineView - Chronological event timeline
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useDatasetView } from '../base/useDatasetView';
import { useViewMetadata } from '../base/useViewMetadata';
import { useBacktrackr } from '@opcentral/backtrackr';
import TimelineEvent from './TimelineEvent';
import './TimelineView.css';

const TimelineView = ({
  viewId = 'timeline-default',
  datasetContext,
  dataContext,
  backtrackrContext: propBacktrackrContext,
  config = {}
}) => {
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [groupBy, setGroupBy] = useState('day'); // day, week, month, year
  const [filterDateRange, setFilterDateRange] = useState(null);
  const [showMilestones, setShowMilestones] = useState(true);
  
  const {
    items,
    fieldMappings,
    getField,
    updateItem,
    addItem,
    deleteItem
  } = useDatasetView('timeline', datasetContext, dataContext);
  
  const hookBacktrackrContext = useBacktrackr();
  const backtrackrContext = propBacktrackrContext || hookBacktrackrContext;
  
  const {
    getMetadata,
    setMetadata
  } = useViewMetadata(viewId, items, updateItem);
  
  // Group events by date
  const groupedEvents = useMemo(() => {
    const sorted = [...items].sort((a, b) => {
      const dateA = new Date(getField(a, 'startDate') || getField(a, 'createdAt'));
      const dateB = new Date(getField(b, 'startDate') || getField(b, 'createdAt'));
      return dateB - dateA; // Most recent first
    });
    
    const groups = {};
    sorted.forEach(item => {
      const date = new Date(getField(item, 'startDate') || getField(item, 'createdAt'));
      let groupKey;
      
      switch (groupBy) {
        case 'year':
          groupKey = date.getFullYear();
          break;
        case 'month':
          groupKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
          break;
        case 'week':
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay());
          groupKey = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          break;
        default: // day
          groupKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      }
      
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(item);
    });
    
    return groups;
  }, [items, groupBy, getField]);
  
  const handleCreateEvent = useCallback(() => {
    const nameField = fieldMappings['name'];
    const startDateField = fieldMappings['startDate'];
    
    if (!nameField) return;
    
    const newEvent = {
      [nameField]: 'New Event',
      [startDateField]: new Date().toISOString()
    };
    
    addItem(newEvent);
  }, [fieldMappings, addItem]);
  
  return (
    <div className="timeline-view">
      <div className="timeline-toolbar">
        <button onClick={handleCreateEvent} className="btn-new-event">+ New Event</button>
        
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="timeline-group-select">
          <option value="day">By Day</option>
          <option value="week">By Week</option>
          <option value="month">By Month</option>
          <option value="year">By Year</option>
        </select>
        
        <label className="timeline-checkbox">
          <input type="checkbox" checked={showMilestones} onChange={(e) => setShowMilestones(e.target.checked)} />
          Show Milestones
        </label>
      </div>
      
      <div className="timeline-container">
        <div className="timeline-line" />
        
        {Object.entries(groupedEvents).map(([groupKey, events]) => (
          <div key={groupKey} className="timeline-group">
            <div className="timeline-group-header">
              <h3>{groupKey}</h3>
            </div>
            
            {events.map((event, index) => (
              <TimelineEvent
                key={event.id}
                event={event}
                getField={getField}
                updateItem={updateItem}
                isSelected={selectedEventId === event.id}
                onSelect={() => setSelectedEventId(event.id)}
                side={index % 2 === 0 ? 'left' : 'right'}
                showMilestone={showMilestones && getField(event, 'type') === 'milestone'}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimelineView;
